import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import { FaSignOutAlt } from 'react-icons/fa';
import { io } from 'socket.io-client';
import { fetchJson } from '../../services/apiClient';
import { enviarAnexoChat } from '../../services/atendimentoApi';
import { fixListingLinks, cardUrl } from '../../utils/chatLinks';

// Floating Chat Widget connected to Socket.io backend
// Env var: VITE_CHAT_URL (e.g., http://localhost:8090)
const CHAT_URL = import.meta.env.VITE_CHAT_URL;
const STORAGE_KEY = 'jp_chat_messages';
const SESSION_KEY = 'chatSessionId';
const IDENT_KEY = 'chatIdentified';
const TYPING_TEXT = 'Digitando...';
const INITIAL_BOT_MESSAGE = 'Olá! Sou a Sónia, a sua assistente da JoviPlanet 🏡\nComo posso ajudar hoje?';
// Sem fallback visual de "Sem resposta" — removemos placeholders silenciosamente

const styles = {
  launcher: {
    position: 'fixed',
    right: 20,
    bottom: 20,
    width: 56,
    height: 56,
    borderRadius: '50%',
    border: 'none',
    padding: '2.5px',
    boxShadow: '0 6px 18px rgba(0,0,0,.2)',
    background: '#BA824F',
    color: '#fff',
    cursor: 'pointer',
    zIndex: 2147483647,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'auto',
    transition: 'box-shadow .2s, transform .2s',
  },
  launcherInner: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  launcherIcon: {
    width: '72%',
    height: '72%',
    objectFit: 'contain',
  },
  launcherTooltip: {
    position: 'fixed',
    right: 24,
    bottom: 82,
    background: '#1f2937',
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
    padding: '6px 10px',
    borderRadius: 8,
    boxShadow: '0 8px 20px rgba(0,0,0,.25)',
    zIndex: 2147483647,
    pointerEvents: 'none',
  },
  panel: {
    position: 'fixed',
    right: 20,
    bottom: 84,
    width: 360,
    maxWidth: '90vw',
    height: 440,
    maxHeight: '70vh',
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 20px 50px rgba(0,0,0,.25)',
    overflow: 'hidden',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: '#BA824F',
    color: '#fff',
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  statusDot: (on) => ({
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: on ? '#22c55e' : '#ef4444',
    marginRight: 8,
  }),
  body: {
    padding: 12,
    flex: 1,
    overflow: 'auto',
    background: '#fafafa',
  },
  row: (me) => ({
    display: 'flex',
    justifyContent: me ? 'flex-end' : 'flex-start',
    margin: '6px 0',
  }),
  bubble: (me) => ({
    display: 'inline-block',
    background: me ? '#DCF8C6' : '#F1F0F0',
    padding: '8px 10px',
    borderRadius: 12,
    maxWidth: '80%',
    color: '#111',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  }),
  footer: {
    borderTop: '1px solid #eee',
    padding: 8,
    display: 'flex',
    gap: 8,
    background: '#fff',
  },
  input: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: 10,
  },
  send: {
    padding: '10px 14px',
    borderRadius: 10,
    background: '#34574A',
    color: '#fff',
    border: 'none',
  },
  cardsStrip: {
    display: 'flex',
    gap: 8,
    overflowX: 'auto',
    padding: '4px 0 8px',
    marginTop: 4,
  },
  card: {
    flex: '0 0 210px',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  cardImg: {
    width: '100%',
    aspectRatio: '16/10',
    objectFit: 'cover',
    background: '#eef2f0',
    display: 'block',
  },
  cardBody: { padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4 },
  cardTitle: {
    fontWeight: 700, fontSize: 13, color: '#1f2937', lineHeight: 1.25,
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
  },
  cardLoc: { fontSize: 11, color: '#6b7280' },
  cardPrice: { fontSize: 14, fontWeight: 700, color: '#34574A' },
  cardChips: { fontSize: 11, color: '#4b5563' },
  cardBtn: {
    marginTop: 4, padding: '6px 10px', borderRadius: 8, border: 'none',
    background: '#BA824F', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    textAlign: 'center',
  },
};

// sessionId persistente — mantém o histórico no servidor após reconexão
const getSessionId = () => {
  try {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `sess-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch { return undefined; }
};

// Contexto da página atual: imóvel ou reserva abertos no momento do envio
const getPageContext = () => {
  try {
    const path = window.location.pathname || '';
    const imovel = path.match(/\/property\/(\d+)/);
    const reserva = path.match(/\/reservations\/(\d+)/);
    return {
      imovelId: imovel ? Number(imovel[1]) : undefined,
      reservaId: reserva ? Number(reserva[1]) : undefined,
    };
  } catch { return {}; }
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [launcherHover, setLauncherHover] = useState(false);
  const [connected, setConnected] = useState(false);
  const [input, setInput] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [handoff, setHandoff] = useState(null); // { ticketId, conversaId } após transbordo
  const [atendente, setAtendente] = useState(null); // nome do atendente humano ativo
  const [needsIdent, setNeedsIdent] = useState(false); // backend pede identificação de anônimos
  const [identified, setIdentified] = useState(() => {
    try { return Boolean(localStorage.getItem(IDENT_KEY)); } catch { return false; }
  });
  const [ident, setIdent] = useState({ nome: '', email: '', whatsapp: '' });
  const [protocolo, setProtocolo] = useState(null);
  const [rating, setRating] = useState(0); // 0 = não exibir; -1 = avaliado
  const [ratingOpen, setRatingOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  // carrega o histórico na inicialização (evita a corrida de gravar [] antes do load)
  const [messages, setMessages] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];
      // converte mensagens legadas de "Sem resposta" para placeholder 'Digitando...'
      return arr.map(m => (
        m && m.from === 'bot' && typeof m.text === 'string' && m.text.toLowerCase().startsWith('sem resposta')
          ? { id: (m.id || Date.now() + ':tempfix'), from: 'bot', text: TYPING_TEXT, temp: true }
          : m
      ));
    } catch { return []; }
  }); // { id, from: 'me'|'bot', text, temp?, cards? }
  const socketRef = useRef(null);
  const boxRef = useRef(null);
  const pendingIdRef = useRef(null);
  const pendingTimerRef = useRef(null);
  const initialTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  // true após 'connect': a primeira 'resposta' da conexão é a saudação automática do servidor
  const suppressGreetingRef = useRef(false);

  // Remove tags like <result>...</result> from bot messages before rendering
  const cleanBotText = (text) => {
    if (typeof text !== 'string') return '';
    try {
      // Remove opening/closing <result> tags (case-insensitive)
      let t = text.replace(/<\/?result\s*>/gi, '');
      // Optionally, strip stray unmatched tags "<result>" or "</result>" left around
      t = t.replace(/<\/?result[^>]*>/gi, '');
      return t.trim();
    } catch {
      return text;
    }
  };

  // Persist history to localStorage (cap 200 msgs)
  useEffect(() => {
    try {
      const capped = Array.isArray(messages) ? messages.slice(-200) : [];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(capped));
    } catch { /* noop */ }
  }, [messages]);

  // auto scroll
  useEffect(() => {
    if (boxRef.current) {
      boxRef.current.scrollTop = boxRef.current.scrollHeight;
    }
  }, [messages, open]);

  // Mensagem inicial automática ao abrir o chat (somente se não houver histórico)
  useEffect(() => {
    if (!open) {
      if (initialTimerRef.current) {
        try { clearTimeout(initialTimerRef.current); } catch { /* noop */ }
        initialTimerRef.current = null;
      }
      return;
    }
    if (Array.isArray(messages) && messages.length > 0) return;

    const tempId = Date.now() + ':bot:init:typing';
    setMessages([{ id: tempId, from: 'bot', text: TYPING_TEXT, temp: true }]);

    if (initialTimerRef.current) {
      try { clearTimeout(initialTimerRef.current); } catch { /* noop */ }
      initialTimerRef.current = null;
    }
    initialTimerRef.current = setTimeout(() => {
      setMessages((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) {
          return [{ id: Date.now() + ':bot:init', from: 'bot', text: INITIAL_BOT_MESSAGE }];
        }
        // Substitui o placeholder inicial se ainda estiver lá
        const last = prev[prev.length - 1];
        if (last && last.id === tempId) {
          const clone = [...prev];
          clone[clone.length - 1] = { id: Date.now() + ':bot:init', from: 'bot', text: INITIAL_BOT_MESSAGE };
          return clone;
        }
        // Se o usuário já enviou algo, não injeta mensagem inicial
        return prev;
      });
      initialTimerRef.current = null;
    }, 4000);
  }, [open, messages]);

  // Saneamento contínuo: se alguma mensagem 'Sem resposta' aparecer, converte para 'Digitando...'
  useEffect(() => {
    const hasLegacy = messages.some(m => m && m.from === 'bot' && typeof m.text === 'string' && m.text.toLowerCase().startsWith('sem resposta'));
    if (hasLegacy) {
      setMessages(prev => prev.map(m => (m && m.from==='bot' && typeof m.text === 'string' && m.text.toLowerCase().startsWith('sem resposta'))
        ? { id: (m.id || Date.now()+':tempfix2'), from: 'bot', text: TYPING_TEXT, temp: true }
        : m
      ));
    }
  }, [messages]);

  // connect socket
  useEffect(() => {
    try { console.log('[ChatWidget] mounting, CHAT_URL =', CHAT_URL); } catch { /* noop */ }
    const socket = io(CHAT_URL, {
      auth: {
        token: localStorage.getItem('auth_token') || undefined,
        sessionId: getSessionId(),
      },
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      suppressGreetingRef.current = true;
      try {
        // Optionally send user metadata
        const token = localStorage.getItem('auth_token') || '';
        socket.emit('hello', { token });
      } catch { /* noop */ }
    });

    socket.on('disconnect', () => setConnected(false));
    // servidor pode sinalizar que está processando
    socket.on('typing', () => {
      // renova/mostra placeholder de digitação e estende o timeout
      setMessages((prev) => {
        const already = prev.some(m => m.temp === true || (m.from==='bot' && m.text === TYPING_TEXT));
        if (already) return prev;
        return [...prev, { id: Date.now() + ':temp', from: 'bot', text: TYPING_TEXT, temp: true }];
      });
      // não iniciar timeout: manter 'Digitando...' até vir resposta
      if (pendingTimerRef.current) { try { clearTimeout(pendingTimerRef.current); } catch { /* noop */ } }
      pendingTimerRef.current = null;
    });
    // servidor pode avisar erro explícito
    socket.on('erro', () => {
      suppressGreetingRef.current = false;
      // remove qualquer placeholder de digitação sem exibir mensagem de erro
      setMessages((prev) => prev.filter(m => !(m.from==='bot' && (m.temp === true || m.text === TYPING_TEXT))));
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
      pendingIdRef.current = null;
    });
    // visitante identificado pelo formulário
    socket.on('identificado', (p) => {
      if (p && p.ok) {
        try { localStorage.setItem(IDENT_KEY, '1'); } catch { /* noop */ }
        setIdentified(true);
        setNeedsIdent(false);
        if (p.protocolo) setProtocolo(p.protocolo);
      }
    });
    // atendimento humano em andamento (início/mensagem/fim)
    socket.on('atendimento', (p) => {
      const tipo = p?.tipo;
      if (tipo === 'inicio') {
        setAtendente(p?.atendente?.nome || p?.atendente || 'Atendente');
        setRatingOpen(false);
      } else if (tipo === 'fim') {
        setAtendente(null);
        setRatingOpen(true); // oferece avaliação ao final do atendimento
      }
    });
    socket.on('handoff', (payload) => {
      setHandoff(payload && typeof payload === 'object' ? payload : {});
      setMessages((prev) => {
        const text = 'Você foi transferido para um assistente humano. Um atendente irá responder em breve.';
        const idx = [...prev].reverse().findIndex(m => m.from === 'bot' && (m.temp === true || m.text === TYPING_TEXT));
        if (idx !== -1) {
          const clone = [...prev];
          clone[prev.length - 1 - idx] = { ...clone[prev.length - 1 - idx], text, temp: false };
          return clone;
        }
        return [...prev, { id: Date.now() + ':handoff', from: 'bot', text }];
      });
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
      pendingIdRef.current = null;
    });
    // cards de imóveis — chegam DEPOIS da 'resposta'; associar à última msg do bot
    socket.on('imoveis', (cards) => {
      if (!Array.isArray(cards) || cards.length === 0) return;
      setMessages((prev) => {
        // última mensagem do bot (ignora placeholder de digitação)
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          const m = prev[i];
          if (m.from === 'bot' && m.temp !== true) {
            const clone = [...prev];
            clone[i] = { ...clone[i], cards };
            return clone;
          }
        }
        return [...prev, { id: Date.now() + ':cards', from: 'bot', text: '', cards }];
      });
    });
    socket.on('resposta', (texto) => {
      const content = String(texto || '');
      const isAutoGreeting = suppressGreetingRef.current;
      suppressGreetingRef.current = false;
      setMessages((prev) => {
        // saudação automática do servidor (repetida a cada conexão): ignora se já há histórico
        if (isAutoGreeting && prev.length > 0) return prev;
        // Procura o ÚLTIMO placeholder do bot para substituir
        const isPlaceholder = (m) => m.from === 'bot' && (
          m.temp === true || m.text === TYPING_TEXT || (typeof m.text === 'string' && m.text.toLowerCase().startsWith('sem resposta'))
        );
        let idx = -1;
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          if (isPlaceholder(prev[i])) { idx = i; break; }
        }
        if (idx !== -1) {
          const clone = [...prev];
          clone[idx] = { ...clone[idx], text: content, temp: false };
          return clone;
        }
        // Caso não haja placeholder, apenas adiciona
        return [...prev, { id: Date.now() + ':bot', from: 'bot', text: content }];
      });
      // Limpa timer de timeout, se houver
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
      pendingIdRef.current = null;
    });

    return () => socket.disconnect();
  }, []);

  // Config pública do chat — verifica se visitante anônimo precisa se identificar
  useEffect(() => {
    if (!open || identified || localStorage.getItem('auth_token')) return;
    let alive = true;
    (async () => {
      try {
        const { res, data } = await fetchJson('/api/chat/ai/config/publica');
        if (alive && res.ok && data && (data.solicitar_identificacao === true || data?.config?.solicitar_identificacao === true)) {
          setNeedsIdent(true);
        }
      } catch { /* noop */ }
    })();
    return () => { alive = false; };
  }, [open, identified]);

  const enviarIdent = () => {
    if (!ident.nome.trim() || !ident.email.trim() || !socketRef.current) return;
    try {
      socketRef.current.emit('identificar', {
        sessionId: getSessionId(),
        nome: ident.nome.trim(),
        email: ident.email.trim(),
        whatsapp: ident.whatsapp.trim() || undefined,
        paginaOrigem: window.location.href,
      });
    } catch { /* noop */ }
  };

  const onAttach = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || uploading) return;
    if (file.size > 10 * 1024 * 1024) {
      setMessages((prev) => [...prev, { id: Date.now() + ':sys', from: 'bot', text: 'Arquivo muito grande (máx. 10MB).' }]);
      return;
    }
    setUploading(true);
    try {
      const { res, data } = await enviarAnexoChat({ file, sessionId: getSessionId(), clientMsgId: crypto.randomUUID?.() });
      if (res.ok) {
        const url = data?.mensagem?.anexoUrl || data?.anexoUrl || data?.url || '';
        setMessages((prev) => [...prev, { id: Date.now() + ':me', from: 'me', text: url ? `📎 ${file.name}\n${url}` : `📎 ${file.name}` }]);
      } else {
        setMessages((prev) => [...prev, { id: Date.now() + ':sys', from: 'bot', text: 'Não consegui enviar o anexo. Tente novamente.' }]);
      }
    } catch {
      setMessages((prev) => [...prev, { id: Date.now() + ':sys', from: 'bot', text: 'Não consegui enviar o anexo. Tente novamente.' }]);
    } finally {
      setUploading(false);
    }
  };

  const avaliar = (nota) => {
    setRating(nota);
    setRatingOpen(false);
    try {
      socketRef.current?.emit('avaliar', { sessionId: getSessionId(), nota });
    } catch { /* noop */ }
  };

  // Finalizar conversa efetivamente (sem alert nativo)
  const endConversation = () => {
    try {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
      pendingIdRef.current = null;
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(SESSION_KEY); // próxima conversa inicia nova sessão
      localStorage.removeItem(IDENT_KEY);
      setIdentified(false);
      setAtendente(null);
      setProtocolo(null);
      setRatingOpen(false);
      setRating(0);
      setMessages([]);
      if (socketRef.current) {
        try { socketRef.current.emit('end'); } catch { /* noop */ }
      }
      setConfirmOpen(false);
      setHandoff(null);
      setOpen(false); // minimiza
    } catch { /* noop */ }
  };

  const send = () => {
    const text = input.trim();
    if (!text || !socketRef.current) return;
    try {
      socketRef.current.emit('mensagem', {
        message: text,
        sessionId: getSessionId(),
        clientMsgId: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `m-${Date.now()}`,
        token: localStorage.getItem('auth_token') || undefined,
        ...getPageContext(),
      });
    } catch { /* noop */ }
    // adiciona minha mensagem
    setMessages((prev) => [...prev, { id: Date.now() + ':me', from: 'me', text }]);
    // adiciona placeholder de 'digitando...'
    const tempId = Date.now() + ':temp';
    pendingIdRef.current = tempId;
    setMessages((prev) => [...prev, { id: tempId, from: 'bot', text: TYPING_TEXT, temp: true }]);
    // não iniciar timeout: manter 'Digitando...' até vir resposta
    if (pendingTimerRef.current) { try { clearTimeout(pendingTimerRef.current); } catch { /* noop */ } }
    pendingTimerRef.current = null;
    setInput('');
  };

  const ui = (
    <>
      <button
        type="button"
        aria-label="Abrir chat"
        style={{
          ...styles.launcher,
          ...(launcherHover ? { boxShadow: '0 10px 26px rgba(0,0,0,.28)', transform: 'translateY(-2px)' } : {}),
        }}
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setLauncherHover(true)}
        onMouseLeave={() => setLauncherHover(false)}
      >
        <span style={styles.launcherInner}>
          <img src="/favicon-64.png" alt="" style={styles.launcherIcon} />
        </span>
      </button>
      {launcherHover && !open && (
        <div style={styles.launcherTooltip}>Chat online</div>
      )}

      {/* Panel */}
      {open && (
        <div style={styles.panel}>
          <div style={styles.header}>
            <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
              <span style={styles.statusDot(connected)} />
              <strong style={{ whiteSpace: 'nowrap' }}>Chat</strong>
              {atendente && (
                <small style={{ marginLeft: 8, background: 'rgba(255,255,255,.22)', padding: '2px 8px', borderRadius: 8, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>
                  {atendente}
                </small>
              )}
              {!atendente && handoff && (
                <small style={{ marginLeft: 8, background: 'rgba(255,255,255,.22)', padding: '2px 8px', borderRadius: 8, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Humano
                </small>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: '0 0 auto' }}>
              <button
                type="button"
                title="Finalizar conversa"
                aria-label="Finalizar conversa"
                onClick={() => setConfirmOpen(true)}
                style={{ background: 'transparent', color: '#fff', border: 'none', fontSize: 15, cursor: 'pointer', padding: '6px', borderRadius: 6, display: 'inline-flex', alignItems: 'center' }}
              >
                <FaSignOutAlt />
              </button>
              <button type="button" onClick={() => setOpen(false)} aria-label="Fechar chat" style={{ background: 'transparent', color: '#fff', border: 'none', fontSize: 18, cursor: 'pointer', padding: '6px' }}>×</button>
            </div>
          </div>
          {/* Modal de confirmação */}
          {confirmOpen && (
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2147483647 }}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 16, width: 300, boxShadow: '0 10px 30px rgba(0,0,0,.25)' }}>
                <div style={{ fontWeight: 700, color: '#2d3748', marginBottom: 8 }}>Finalizar conversa?</div>
                <div style={{ color: '#4a5568', fontSize: 14, marginBottom: 14 }}>Isso apagará o histórico desta conversa neste dispositivo.</div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button type="button" onClick={() => setConfirmOpen(false)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#374151' }}>Cancelar</button>
                  <button type="button" onClick={endConversation} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: '#BA824F', color: '#fff' }}>Finalizar</button>
                </div>
              </div>
            </div>
          )}
          {/* Formulário de identificação (visitante anônimo, exigido pelo backend) */}
          {needsIdent && !identified && (
            <div style={{ padding: 16, background: '#fff', borderBottom: '1px solid #eee', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1f2937' }}>Antes de começar, se apresente 🙂</div>
              <input
                placeholder="Seu nome *"
                value={ident.nome}
                onChange={(e) => setIdent(p => ({ ...p, nome: e.target.value }))}
                style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13 }}
              />
              <input
                placeholder="Seu e-mail *"
                type="email"
                value={ident.email}
                onChange={(e) => setIdent(p => ({ ...p, email: e.target.value }))}
                style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13 }}
              />
              <input
                placeholder="WhatsApp (opcional)"
                value={ident.whatsapp}
                onChange={(e) => setIdent(p => ({ ...p, whatsapp: e.target.value }))}
                style={{ padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13 }}
              />
              <button
                type="button"
                onClick={enviarIdent}
                disabled={!ident.nome.trim() || !ident.email.trim()}
                style={{ padding: '9px 12px', borderRadius: 8, border: 'none', background: '#34574A', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
              >
                Iniciar atendimento
              </button>
            </div>
          )}
          <div ref={boxRef} style={styles.body}>
            {/* CSS local para animação dos três pontos */}
            <style>{`
              @keyframes chatDot { 0% { opacity: .2 } 20% { opacity: 1 } 100% { opacity: .2 } }
              .chat-typing { display: inline-flex; align-items: center; gap: 8px; }
              .chat-typing .dot { width: 6px; height: 6px; border-radius: 50%; background: #34574A; animation: chatDot 1.2s infinite; }
              .chat-typing .dot:nth-child(2) { animation-delay: .2s; }
              .chat-typing .dot:nth-child(3) { animation-delay: .4s; }
              .chat-md p { margin: 0 0 4px; }
              .chat-md p:last-child { margin-bottom: 0; }
              .chat-md a { color: #34574A; font-weight: 600; }
              .chat-md ul, .chat-md ol { margin: 4px 0; padding-left: 18px; }
              .chat-md strong { color: #111827; }
            `}</style>
            {messages.map((m, i) => (
              <React.Fragment key={i}>
                {!(m.from === 'bot' && !m.temp && !(m.text || '').trim()) && (
                  <div style={styles.row(m.from === 'me')}>
                    <span style={styles.bubble(m.from === 'me')}>
                      {m.temp ? (
                        <span className="chat-typing">
                          <span className="dot" />
                          <span className="dot" />
                          <span className="dot" />
                          <span style={{ color: '#6b7280', marginLeft: 4 }}>Digitando...</span>
                        </span>
                      ) : (
                        m.from === 'bot'
                          ? <div className="chat-md"><ReactMarkdown>{cleanBotText(fixListingLinks(m.text))}</ReactMarkdown></div>
                          : m.text
                      )}
                    </span>
                  </div>
                )}
                {Array.isArray(m.cards) && m.cards.length > 0 && (
                  <div style={styles.cardsStrip}>
                    {m.cards.map((c, ci) => (
                      <div key={c.id || ci} style={styles.card}>
                        {c.foto ? (
                          <img src={c.foto} alt={c.titulo || 'Imóvel'} style={styles.cardImg} loading="lazy" />
                        ) : (
                          <div style={{ ...styles.cardImg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 24 }}>🏠</div>
                        )}
                        <div style={styles.cardBody}>
                          {c.disponivel === false && (
                            <div style={{ alignSelf: 'flex-start', background: '#fef3c7', color: '#92400e', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 6 }}>
                              Temporariamente indisponível
                            </div>
                          )}
                          <div style={styles.cardTitle}>{c.titulo || 'Imóvel'}</div>
                          {c.localizacao && <div style={styles.cardLoc}>📍 {c.localizacao}</div>}
                          {c.preco && <div style={styles.cardPrice}>{c.preco}</div>}
                          <div style={styles.cardChips}>
                            {c.quartos != null && `🛏 ${c.quartos}`}
                            {c.casasDeBanho != null && ` · 🛁 ${c.casasDeBanho}`}
                            {c.capacidade != null && ` · 👥 ${c.capacidade}`}
                          </div>
                          <button
                            type="button"
                            style={styles.cardBtn}
                            onClick={() => {
                              try { window.open(cardUrl(c), '_blank'); } catch { /* noop */ }
                            }}
                          >
                            Ver imóvel
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          {protocolo && (
            <div style={{ padding: '4px 12px', fontSize: 11, color: '#6b7280', background: '#f9fafb', borderTop: '1px solid #eee' }}>
              Protocolo: {protocolo}
            </div>
          )}
          {ratingOpen && (
            <div style={{ padding: '8px 12px', background: '#f9fafb', borderTop: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: '#374151', flex: 1 }}>Como foi seu atendimento?</span>
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} type="button" onClick={() => avaliar(n)}
                  style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: n <= rating ? '#f59e0b' : '#d1d5db' }}>★</button>
              ))}
              <button type="button" onClick={() => setRatingOpen(false)} style={{ border: 'none', background: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 14 }}>×</button>
            </div>
          )}
          <div style={styles.footer}>
            <input
              type="file"
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              ref={(el) => { fileInputRef.current = el; }}
              onChange={onAttach}
            />
            <button
              type="button"
              title="Anexar arquivo"
              onClick={() => fileInputRef.current?.click()}
              disabled={!connected || uploading || (needsIdent && !identified)}
              style={{ ...styles.send, padding: '10px 10px', background: '#e5e7eb', color: '#34574A' }}
            >
              📎
            </button>
            <input
              style={styles.input}
              placeholder={connected ? (needsIdent && !identified ? 'Identifique-se acima para iniciar...' : 'Digite sua mensagem...') : 'Conectando...'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              disabled={!connected || (needsIdent && !identified)}
            />
            <button type="button" style={styles.send} onClick={send} disabled={!connected || (needsIdent && !identified)}>Enviar</button>
          </div>
        </div>
      )}
    </>
  );

  // Render via portal para evitar contextos de stacking do layout
  if (typeof document !== 'undefined' && document.body) {
    return ReactDOM.createPortal(ui, document.body);
  }
  return ui;
}
